import { CalendarDays, Building2, Briefcase, Clock, LogOut, PenLine, Repeat } from '../../icons';
import type { LucideIcon } from '../../icons';

/**
 * 근태에서 **신청서를 내는 길** (2026-09-03 · 정본 P3-3 → 2026-09-06 · 판 A 로 레일에 올림).
 *
 * ★★ 판 A 에서 이 목록은 **본문이 아니라 좌측 레일**이 쓴다(`AttShell`). 화면을 옮겨도 남아,
 *    「근태 현황」을 보다가도 그 자리에서 신청서를 낼 수 있다.
 *
 * ★★★ **여기 있는 여섯은 전자결재에 실재하는 양식이다.** 지어낸 버튼이 아니다 —
 *    `apps/chat-server/src/modules/approval/default-form-schemas.ts` 의 `category: 'HR'`
 *    여섯과 코드가 1:1 이다. 옛 `HRPage` 의 「+ 휴가 신청」은 **눌러도 아무 일이 없는 버튼**
 *    이었고(HR-005 미구현), 그것을 옮겨 오지 않으려고 목록을 한곳에 못 박는다.
 *
 * ★★ **화면이 목록을 판정하지 않는다.** 회사가 양식을 껐거나 이름을 바꿨을 수 있는데,
 *    그 판정은 기안 화면(`ApprovalNewPage`)이 서버 목록으로 한다. 여기 코드가 그쪽 목록에
 *    없으면 기안 화면은 **평소처럼 고르는 첫 걸음**을 보여 준다 — 깨지지 않는다.
 */
export interface HrRequestForm {
  /** 전자결재 문서 종류 코드. */
  code: string;
  label: string;
  icon: LucideIcon;
}

/**
 * ★★★ **자주 내는 것이 위에 온다** (2026-09-06 · 판 A · 사용자 지시).
 *
 *    사용자가 꼽은 셋(초과근로 · 근무시간 수정 · 근무지 외)을 위에 두고, 나머지를 아래 둔다.
 *    **빼지는 않는다** — 휴가는 가장 많이 내는 신청이고, 목록에서 사라지면
 *    「휴가를 어디서 내나」가 화면에서 없어진다.
 * ★ 가르는 것은 선 하나다(`FORM_GROUP_AT`) — 묶음 이름을 붙이면 레일이 한 단 더 깊어진다.
 */
export const HR_REQUEST_FORMS: readonly HrRequestForm[] = [
  { code: 'OVERTIME', label: '초과근로', icon: Clock },
  { code: 'ATTENDANCE_FIX', label: '근무시간 수정', icon: PenLine },
  { code: 'OUTSIDE_WORK', label: '근무지 외', icon: Building2 },
  { code: 'LEAVE', label: '휴가', icon: CalendarDays },
  { code: 'BUSINESS_TRIP', label: '출장', icon: Briefcase },
  { code: 'EARLY_LEAVE', label: '조퇴', icon: LogOut },
];

/**
 * 위 목록에서 **선을 긋는 자리** — 앞의 셋이 「자주 내는 것」이다.
 * ★ 휴일대체(`SUBSTITUTE_LEAVE`)는 회사가 켠 곳에서만 서고 **후보 목록이 필요해 팝업**으로 연다.
 *   레일이 **켠 회사에서만** 이 자리(넷째)에 끼워 넣는다 — 아래 `SUBSTITUTE_FORM`.
 */
export const FORM_GROUP_AT = 3;

/**
 * 휴일대체(보상휴가 §57) — **일곱째이지만 다른 길로 간다** (2026-09-06 · 판 B).
 *
 * ★★★ **양식만으로는 못 내는 신청이다.** 위 여섯은 「무엇을 · 언제」를 사람이 적으면 되지만,
 *    이것은 **「어느 날 근로를 바꿀 수 있는가」를 서버가 알려 줘야** 한다 — 승인된 연장 ·
 *    휴일근로가 있는 날만 대상이고, 그날이 몇 시간으로 바뀌는지는 가산 배수가 정한다.
 *    그래서 기안 화면이 아니라 **후보를 보여 주는 팝업**을 연다.
 * ★★ **회사가 켠 곳에서만 선다.** §57 은 「근로자대표와의 서면 합의」로 여는 제도라 기본이
 *    꺼져 있다 — 안 켠 회사에 세우면 눌러도 「이 회사는 안 씁니다」밖에 못 말한다.
 *    **할 수 없는 일을 메뉴에 세우면 그게 잘못된 안내다.**
 * ★ 문서 종류(`SUBSTITUTE_LEAVE`)는 다른 여섯과 같이 **전자결재에 실재하는 양식**이다 —
 *   기안 화면으로 내도 원장 줄이 생긴다(판 B 가 이었다). 다만 후보를 못 보고 내게 된다.
 */
export const SUBSTITUTE_FORM: HrRequestForm = {
  code: 'SUBSTITUTE_LEAVE', label: '보상휴가', icon: Repeat,
};

/** 달마다 보는 화면 — 연차 · 가산 시간 · 낸 신청 · 임금이 여기 모인다. */
export const MY_HISTORY_PATH = '/erp/attendance/me';

/**
 * 양식을 미리 고른 채 기안 화면을 연다.
 * ★ 코드를 그대로 싣지 않고 인코딩한다 — 회사가 지은 코드에 무엇이 들었는지 모른다.
 */
export function approvalDraftPath(code: string): string {
  return `/approval/new?type=${encodeURIComponent(code)}`;
}
